import * as React from 'react';
import { useEffect, useReducer, useState } from 'react';
import { channelValues } from 'shared/src/domain/line-item/line-item-channels-filters';
import { StandardLineItemApiCreateRequest } from 'shared/src/api/line-item-api-types';
import {
  StandardLineItemCalculatedFields,
  StandardLineItemMediaTeamCalculatedFields
} from '@/features/line-items/line-item-form/standard/standard-line-item-calculated-fields';
import { useUnsavedChangesWarning } from '@/features/line-items/line-item-form/ui/use-unsaved-changes-warning';
import { useScrollToFirstError } from '@/features/line-items/line-item-form/ui/use-scroll-to-first-error';
import { AdInfoSection } from '../ui/ad-info-section';
import { BillableRevenueBanner } from '../ui/billable-revenue-banner';
import { ButtonSelect } from '../ui/button-select';
import { DatesField } from '../ui/dates-field';
import { LockedChannelTacticType } from '../ui/locked-channel-tactic-type';
import { MediaTeamSection } from '../ui/media-team-section';
import { MoneyField } from '../ui/money-field';
import { NameSection } from '../ui/name-section';
import { NotesSection } from '../ui/notes-section';
import { Section } from '../ui/section';
import { TextField } from '../ui/text-field';
import {
  StandardFormState,
  standardLineItemReducer
} from '@/features/line-items/line-item-form/common/form-reducer';
import {
  allocationSummary,
  isDirtyStandard,
  selectAvailableTactics,
  selectAvailableUnitPriceTypes,
  selectChannelName,
  selectDisplayCPM,
  selectIsLocked,
  selectPacingBlockRows,
  selectPriceAllocation,
  hasValidationErrors,
  selectStandardValidationErrors,
  selectTacticName,
  selectTotalSpend,
  selectUnitPriceTypeName
} from '@/features/line-items/line-item-form/common/selectors';
import { Pacing } from '@/features/line-items/line-item-form/ui/pacing';
import { formatMoney } from 'shared/src/utils/money/money-utils';
import { daysBetween } from 'shared/src/date-string-utils/date-utils';
import { toStandardApiRequest } from '@/features/line-items/line-item-form/common/form-state';
import { unitShortLabel } from '@/features/line-items/line-item-form/utils/pacing-display';
import { useChannelRatesContext } from '@/features/strategy/channel-rates-context';

type Props = {
  initialState: StandardFormState;
  onSubmit: (request: StandardLineItemApiCreateRequest) => void | Promise<void>;
  onCancel: () => void;
  typeSelector?: React.ReactNode;
  onFormChange?: (isDirty: boolean) => void;
};

export function StandardLineItemForm({
  initialState,
  onSubmit,
  onCancel,
  typeSelector,
  onFormChange
}: Props) {
  const { unitPriceTypes } = useChannelRatesContext();
  const [state, dispatch] = useReducer(standardLineItemReducer, initialState);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isLocked = selectIsLocked(state);
  const errors = selectStandardValidationErrors(state);
  const hasErrors = hasValidationErrors(errors);
  const isDirty = isDirtyStandard(state);
  useUnsavedChangesWarning(isDirty);
  useScrollToFirstError(errors, submitAttempted);
  useEffect(() => {
    onFormChange?.(isDirty);
  }, [isDirty, onFormChange]);
  const priceAllocation = selectPriceAllocation(state);
  const billableTotal = selectTotalSpend(state);
  const showBillable = isLocked && billableTotal > 0;

  const errorFor = (field: string): string | undefined => {
    if (!submitAttempted && !state.touched.has(field)) return undefined;
    return errors.fieldErrors[field];
  };

  const warningFor = (field: string): string | undefined => errors.fieldWarnings[field];

  const rootErrors = submitAttempted ? errors.formErrors : [];
  const rootWarnings = [
    ...Object.entries(errors.fieldWarnings)
      .filter(([key]) => !/^pacing_blocks\.\d+\.budget$/.test(key))
      .map(([, value]) => value),
    ...errors.formWarnings
  ];

  const channelOptions = toOptions(channelValues(state.channels));
  const tacticOptions = toOptions(selectAvailableTactics(state));
  const unitPriceTypeOptions = toOptions(selectAvailableUnitPriceTypes(state));

  const unitPriceDisplay = selectDisplayCPM(state, state.formData.unit_price);
  const isCPM = selectUnitPriceTypeName(state) === 'CPM';

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitAttempted(true);
    if (hasErrors) return;
    const parsed = StandardLineItemApiCreateRequest.safeParse(toStandardApiRequest(state));
    if (!parsed.success) return;
    setIsSubmitting(true);
    try {
      await onSubmit(parsed.data);
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleCancel() {
    dispatch({ type: 'RESET', initialState });
    setSubmitAttempted(false);
    onCancel();
  }

  return (
    <form onSubmit={handleSubmit} className="@container flex flex-col p-6 pt-0">
      <NameSection
        name={state.formData.name}
        mode={state.mode}
        isSubmitting={isSubmitting}
        submitDisabled={false}
        allocatedPrice={priceAllocation.allocated ?? state.proposalInfo.allocated_price}
        totalPrice={priceAllocation.total}
        plannedMargin={state.proposalInfo.planned_margin}
        targetMargin={state.proposalInfo.target_margin}
        rootErrors={rootErrors}
        rootWarnings={rootWarnings}
        hasErrors={hasErrors}
        submitAttempted={submitAttempted}
        nameError={errorFor('name')}
        onNameChange={value => dispatch({ type: 'SET_NAME', value })}
        onNameBlur={() => dispatch({ type: 'BLUR_FIELD', field: 'name' })}
        onCancel={handleCancel}
        typeSelector={typeSelector}
      />
      <Section heading="Pricing">
        <div>
          <MoneyField
            id="line-item-price"
            label="Price"
            value={state.formData.price}
            decimalScale={2}
            onChange={value => dispatch({ type: 'SET_PRICE', value })}
            onBlur={() => dispatch({ type: 'BLUR_FIELD', field: 'price' })}
            error={errorFor('price')}
          />
          {showBillable ? <BillableRevenueBanner total={billableTotal} /> : null}
        </div>
        <TextField
          id="line-item-purchase-order-number"
          label="PO Number (Optional)"
          value={state.formData.purchase_order_number}
          onChange={value =>
            dispatch({
              type: 'SET_PO_NUMBER',
              value: value === '' ? undefined : value
            })
          }
          onBlur={() => dispatch({ type: 'BLUR_FIELD', field: 'purchase_order_number' })}
          error={errorFor('purchase_order_number')}
        />
        {isLocked ? (
          <>
            <LockedChannelTacticType
              channelName={selectChannelName(state)}
              tacticName={selectTacticName(state)}
              unitPriceTypeName={selectUnitPriceTypeName(state)}
            />
            <MoneyField
              id="line-item-unit-price"
              label="Unit Price"
              suffix={isCPM ? 'CPM' : 'per unit'}
              value={unitPriceDisplay}
              decimalScale={3}
              description="Defaults to Rate Card Target"
              placeholder="Auto-fills per Unit Price Type"
              disabled={state.formData.unit_price_type_id == null}
              onChange={value =>
                dispatch({
                  type: 'SET_UNIT_PRICE',
                  value: value === undefined ? undefined : isCPM ? value / 1000 : value
                })
              }
              onBlur={() => dispatch({ type: 'BLUR_FIELD', field: 'unit_price' })}
            />
          </>
        ) : (
          <>
            <ButtonSelect<number>
              id="line-item-channel"
              label="Channel"
              description="Select one"
              value={state.formData.channel_id}
              options={channelOptions}
              onChange={channel_id =>
                channel_id !== undefined && dispatch({ type: 'CHANGE_CHANNEL', channel_id })
              }
              error={errorFor('channel_id')}
            />
            <ButtonSelect<number>
              id="line-item-tactic"
              label="Tactic"
              description="Select one"
              placeholder="Select Channel for options"
              value={state.formData.tactic_id}
              options={tacticOptions}
              onChange={tactic_id =>
                tactic_id !== undefined && dispatch({ type: 'CHANGE_TACTIC', tactic_id })
              }
              disabled={state.formData.channel_id == null || tacticOptions.length === 0}
              error={errorFor('tactic_id')}
            />
            <div className="flex flex-row gap-6 [&>*]:flex-1">
              <ButtonSelect<number>
                id="line-item-unit-price-type"
                label="Unit Price Type"
                description="Select one"
                placeholder="Select Tactic for options"
                value={state.formData.unit_price_type_id}
                options={unitPriceTypeOptions}
                onChange={unit_price_type_id =>
                  unit_price_type_id !== undefined &&
                  dispatch({ type: 'CHANGE_UNIT_PRICE_TYPE', unit_price_type_id })
                }
                disabled={state.formData.tactic_id == null || unitPriceTypeOptions.length === 0}
                error={errorFor('unit_price_type_id')}
              />
              <MoneyField
                id="line-item-unit-price"
                label="Unit Price"
                suffix={isCPM ? 'CPM' : 'per unit'}
                value={unitPriceDisplay}
                decimalScale={3}
                description="Defaults to Rate Card Target"
                placeholder="Auto-fills per Unit Price Type"
                disabled={state.formData.unit_price_type_id == null}
                onChange={value =>
                  dispatch({
                    type: 'SET_UNIT_PRICE',
                    value: value === undefined ? undefined : isCPM ? value / 1000 : value
                  })
                }
                onBlur={() => dispatch({ type: 'BLUR_FIELD', field: 'unit_price' })}
                error={errorFor('unit_price')}
              />
            </div>
          </>
        )}
        <StandardLineItemCalculatedFields
          inputs={toCalculationInput(state)}
          isLocked={state.formData.marginLocked}
          onLockMargin={() => dispatch({ type: 'TOGGLE_MARGIN_LOCK' })}
        />
      </Section>
      <Section heading="Dates">
        <DatesField
          id="line-item-dates"
          value={state.formData.dates}
          minDate={state.proposalInfo.campaign_dates.from}
          maxDate={state.proposalInfo.campaign_dates.to}
          onChange={value => dispatch({ type: 'SET_DATES', value })}
          error={errorFor('dates')}
          warning={warningFor('dates')}
        />
      </Section>

      <Pacing
        config={{
          lineItemType: 'standard',
          rows: selectPacingBlockRows(state),
          spend: state.dailyBudgetSpent,
          allocation: allocationSummary(state, state.formData.price),
          monthly_rollover: state.formData.monthly_rollover,
          pacing_amount: state.formData.price,
          unit_cost: state.formData.unit_price,
          dates: state.formData.dates,
          pacing_type: state.formData.pacing_type,
          label: 'Price',
          unit_label: unitShortLabel(state.formData.unit_price_type_id, unitPriceTypes),
          is_published: isLocked
        }}
        dispatch={dispatch}
        pacingDescription={<PacingDescription state={state} />}
        errorFor={errorFor}
        warningFor={warningFor}
      />

      <AdInfoSection state={state} dispatch={dispatch} errorFor={errorFor} />
      <NotesSection state={state} dispatch={dispatch} errorFor={errorFor} />
      <MediaTeamSection
        state={state}
        dispatch={dispatch}
        errorFor={errorFor}
        calculatedFields={
          <StandardLineItemMediaTeamCalculatedFields
            inputs={toCalculationInput(state)}
            isLocked={state.formData.marginLocked}
            onLockMargin={() => dispatch({ type: 'TOGGLE_MARGIN_LOCK' })}
          />
        }
      />
    </form>
  );
}

function PacingDescription({ state }: { state: StandardFormState }) {
  const { price, dates } = state.formData;
  const dayCount = daysBetween(dates.from, dates.to);
  const formattedPrice = price === undefined ? '—' : formatMoney(price);
  return (
    <div className="text-sm text-gray-500">
      Define how you want to distribute spend of the line item's{' '}
      <span className="font-semibold">{formattedPrice} Price</span> across its{' '}
      <span className="font-semibold">{dayCount}</span> day schedule
    </div>
  );
}

function toOptions<V>(items: readonly { id: V; name: string }[]): { value: V; label: string }[] {
  return items.map(item => ({ value: item.id, label: item.name }));
}

function toCalculationInput(state: StandardFormState) {
  return {
    price: state.formData.price,
    unit_price: state.formData.unit_price,
    unit_price_display: selectDisplayCPM(state, state.formData.unit_price),
    unit_price_type_id: state.formData.unit_price_type_id ?? undefined,
    planned_unit_cost: state.formData.planned_unit_cost,
    agency_markup_rate: state.proposalInfo.agency_markup_rate,
    referral_commission_rate: state.proposalInfo.referral_commission_rate
  };
}
